/**
 * The Edit stages dialog's side of routing and the unassigned-ingredient
 * check, as pure functions over the dialog's drafts. A stage's outcome goes
 * into the next stage unless it is held to flow into a later one; a draft
 * names that stage by its local key, which is turned into a position in the
 * saved list only when the stages are sent.
 */

import type { RecipeStage, RecipeStageInput } from "@/lib/api/requests/recipes";
import { heldTargetIndex } from "@/lib/recipes/staged-method";

export interface RoutedDraft {
  /** Local identity, stable across reordering. */
  key: number;
  kind: RecipeStageInput["kind"];
  includeInNext: boolean;
  /** Key of the later STAGE a held outcome flows into; null unless held. */
  flowsIntoKey: number | null;
}

/** The routing a draft starts with: the stage ids of the server mapped onto draft keys. */
export function routingFromStages(
  ordered: RecipeStage[],
  keys: number[],
): Pick<RoutedDraft, "includeInNext" | "flowsIntoKey">[] {
  return ordered.map((_, index) => {
    const target = heldTargetIndex(ordered, index);
    return target === null
      ? { includeInNext: true, flowsIntoKey: null }
      : { includeInNext: false, flowsIntoKey: keys[target]! };
  });
}

/** The STAGEs after `index`, where a held outcome may flow into. */
export function laterStages<T extends Pick<RoutedDraft, "kind">>(drafts: T[], index: number): T[] {
  return drafts.slice(index + 1).filter((draft) => draft.kind === "STAGE");
}

/**
 * The drafts with every route that no longer makes sense put back to "include
 * in next stage": a target that was deleted, that is no longer later than the
 * stage after a reorder, or a route on a task.
 */
export function normalizeRouting<T extends RoutedDraft>(drafts: T[]): T[] {
  let changed = false;
  const next = drafts.map((draft, index) => {
    const valid =
      draft.kind === "STAGE" &&
      !draft.includeInNext &&
      draft.flowsIntoKey !== null &&
      laterStages(drafts, index).some((later) => later.key === draft.flowsIntoKey);
    if (valid || (draft.includeInNext && draft.flowsIntoKey === null)) return draft;
    changed = true;
    return { ...draft, includeInNext: true, flowsIntoKey: null };
  });
  return changed ? next : drafts;
}

/**
 * Where a stage goes when its outcome is first held. The stage after the next
 * one, so that the switch changes something, or the next one when it is the
 * only later stage; null for a stage with no later stage to flow into.
 */
export function defaultHeldTarget<T extends Pick<RoutedDraft, "kind" | "key">>(drafts: T[], index: number): number | null {
  const later = laterStages(drafts, index);
  return (later[1] ?? later[0])?.key ?? null;
}

/** The routing fields of each draft's payload: nothing for the default, a 0-based index otherwise. */
export function routingInputs<T extends RoutedDraft>(
  drafts: T[],
): Pick<RecipeStageInput, "includeInNext" | "flowsInto">[] {
  return drafts.map((draft, index) => {
    if (draft.kind !== "STAGE" || draft.includeInNext || draft.flowsIntoKey === null) return {};
    const target = drafts.findIndex((candidate) => candidate.key === draft.flowsIntoKey);
    // Only a later STAGE is a valid target; anything else is sent as the default.
    return target > index && drafts[target]!.kind === "STAGE" ? { includeInNext: false, flowsInto: target } : {};
  });
}

export interface FlowSummaryEntry {
  /** 1-based place among the stages (tasks do not count). */
  from: number;
  /** 1-based place of the stage it goes into, or null for the finished batch. */
  to: number | null;
  held: boolean;
}

/** "Stage 1 to Stage 4 (held), Stage 2 to Stage 3, Stage 5 to Done", as data. */
export function flowSummary<T extends RoutedDraft>(drafts: T[]): FlowSummaryEntry[] {
  const stages = drafts.filter((draft) => draft.kind === "STAGE");
  return stages.map((draft, index) => {
    const target = draft.includeInNext ? -1 : stages.findIndex((candidate) => candidate.key === draft.flowsIntoKey);
    if (target > index) return { from: index + 1, to: target + 1, held: true };
    return { from: index + 1, to: index + 1 < stages.length ? index + 2 : null, held: false };
  });
}

/**
 * The ingredient lines no STAGE holds. A task never holds one, and a recipe
 * with no stage yet has nowhere to put them, so it has nothing to warn about.
 */
export function unassignedLines<L extends { id: number }>(
  drafts: { kind: RecipeStageInput["kind"]; ingredientIds: number[] }[],
  lines: L[],
): L[] {
  const stages = drafts.filter((draft) => draft.kind === "STAGE");
  if (stages.length === 0) return [];
  const assigned = new Set(stages.flatMap((draft) => draft.ingredientIds));
  return lines.filter((line) => !assigned.has(line.id));
}
